import React, { useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { ScrollArea } from './ui/scroll-area';

export interface PointsTransaction {
  id: string;
  type: 'earn' | 'spend';
  amount: number;
  description: string;
  date: Date;
}

interface PointsDialogProps {
  open: boolean;
  onClose: () => void;
  currentPoints: number;
  canClaimDaily: boolean;
  onClaimDaily: () => void;
  transactions: PointsTransaction[];
}

export function PointsDialog({
  open,
  onClose,
  currentPoints,
  canClaimDaily,
  onClaimDaily,
  transactions,
}: PointsDialogProps) {
  const [showHistory, setShowHistory] = useState(false);

  const handleClaim = () => {
    onClaimDaily();
    toast.success('Daily points claimed! +30 points');
  };

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}/${month}/${day} ${hours}:${minutes}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        {/* Header - Title centered with back button aligned */}
        <div className="relative flex items-center justify-center h-10 mb-4">
          {showHistory && (
            <button
              onClick={() => setShowHistory(false)}
              className="absolute left-0 top-0 p-2 hover:bg-gray-100 rounded-sm transition-colors opacity-70 hover:opacity-100"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <h2 className="font-semibold text-lg">Points Center</h2>
        </div>

        {!showHistory ? (
          <div className="space-y-6">
            {/* Current Points Display - Horizontal Layout */}
            <div className="bg-linear-to-br from-amber-50 to-yellow-50 rounded-2xl p-5 border-2 border-amber-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Current Balance
                </span>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                  <span className="text-3xl font-bold bg-linear-to-r from-amber-600 to-yellow-600 bg-clip-text text-transparent">
                    {currentPoints}
                  </span>
                </div>
              </div>
            </div>

            {/* Daily Check-in - Simplified */}
            <div className="bg-white rounded-xl p-4 border-2 border-border">
              <div className="flex items-center justify-between">
                <span className="text-sm">
                  {canClaimDaily
                    ? 'Claim your daily reward!'
                    : 'Come back tomorrow for more points'}
                </span>
                <Button
                  onClick={handleClaim}
                  disabled={!canClaimDaily}
                  className="bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl flex items-center gap-1.5"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>+30</span>
                </Button>
              </div>
            </div>

            {/* View History Button */}
            <Button
              onClick={() => setShowHistory(true)}
              variant="outline"
              className="w-full rounded-xl"
            >
              Transaction History
            </Button>
          </div>
        ) : (
          <div className="space-y-1">
            <ScrollArea className="h-[400px] pr-4">
              {transactions.length === 0 ? (
                <div className="text-center text-muted-foreground py-12">
                  No transactions yet
                </div>
              ) : (
                <div className="space-y-4">
                  {transactions.map((transaction) => (
                    <div
                      key={transaction.id}
                      className="py-3 border-b border-border last:border-0"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium">
                            {transaction.description}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(transaction.date)}
                          </p>
                        </div>
                        <div
                          className={`text-sm font-medium ml-4 ${
                            transaction.type === 'earn'
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`}
                        >
                          {transaction.type === 'earn' ? '+' : '-'}
                          {transaction.amount}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
